import { useState } from "react"
import { Button } from "@/components/ui/button"
import { AppointmentDialogFooter } from "../AppointmentDialogFooter"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { extractApiError } from "@/lib/api-errors"
import { useAppDispatch } from "@/store"
import { MAX_CANCEL_NOTE_LENGTH, cancelNotesProblem, notesApi } from "../../api/notesApi"
import { useCancelBookingAsStaffMutation } from "../../bookingCalendarApi"
import type { BookingAction, BookingActionFormProps } from "./types"
import { isChangeable } from "./types"
import { useReportBusy } from "./useReportBusy"

/**
 * Cancels the appointment, as staff, with two notes: an internal one (required; kept on the booking
 * and given to Cal as its reason — Cal mails nobody, so that is staff-only too) and an optional one
 * for the customer, which CMS sends them in its own cancellation text, call and email.
 */
export const CancelAction: BookingAction = {
  id: "cancelling",
  label: "Cancel appointment",
  triggerVariant: "destructive",
  triggerClassName: "sm:mr-auto",
  isAvailable: isChangeable,
  Form: CancelForm,
}

/**
 * Releases the slot back to the open pool (AB#769, Q14): the same staff cancellation, armed with
 * `ctx.releaseNotes` — a canned notice for the customer and an internal note saying why — for staff
 * to check before sending. Offered only by a list that supplies those notes; the appointment dialog
 * never does.
 */
export const ReleaseAction: BookingAction = {
  id: "release",
  label: "Release slot",
  triggerVariant: "outline",
  isAvailable: ctx => isChangeable(ctx) && ctx.releaseNotes !== undefined,
  Form: ReleaseForm,
}

function ReleaseForm(props: BookingActionFormProps) {
  return <CancelForm {...props} release />
}

interface CancelFormProps extends BookingActionFormProps {
  /** Starts from `ctx.releaseNotes` and reports "released" rather than "cancelled". */
  release?: boolean
}

function CancelForm({ ctx, onDone, onClose, onBusyChange, onResult, release = false }: CancelFormProps) {
  const [cancelBooking, cancelState] = useCancelBookingAsStaffMutation()
  const dispatch = useAppDispatch()
  const initialNotes = release ? ctx.releaseNotes : undefined
  const [internalNote, setInternalNote] = useState(initialNotes?.internalNote ?? "")
  const [externalNote, setExternalNote] = useState(initialNotes?.externalNote ?? "")
  const [error, setError] = useState<string | null>(null)

  const busy = cancelState.isLoading
  useReportBusy(onBusyChange, busy)

  const { uid, attendeeName } = ctx
  const problem = cancelNotesProblem(internalNote, externalNote)

  async function submitCancellation() {
    if (!uid || problem) return

    setError(null)

    const external = externalNote.trim()
    const result = await cancelBooking({
      bookingUid: uid,
      internalNote: internalNote.trim(),
      externalNote: external.length > 0 ? external : undefined,
    })

    if ("error" in result) {
      const message = describeCancelFailure(result.error)
      setError(message)
      onResult?.({ kind: "failed", detail: message })
      return
    }

    onResult?.({ kind: release ? "released" : "cancelled" })

    // The cancel just wrote this booking's cancellation notes, which the cancel mutation (keyed by
    // Cal uid) cannot invalidate itself.
    if (ctx.requestId) {
      dispatch(notesApi.util.invalidateTags([{ type: "AmsBookingNotes", id: ctx.requestId }]))
    }

    // The grids have been invalidated, so the appointment is on its way off both of them; there is
    // nothing left in here worth reading.
    onClose()
  }

  return (
    <>
      {/* The notes, once somebody has armed the cancellation. Asking for them up front would put two
          text boxes under every appointment anyone opens to read. */}
      <section className="space-y-3">
        {release && (
          <p className="text-xs text-muted-foreground">
            Both notes are filled in with the release notice. Check them, and change them if you need to,
            before releasing the slot.
          </p>
        )}
        <div>
          <Label htmlFor="cancellation-internal-note" className="text-xs font-medium">
            Internal note <span className="text-muted-foreground">(required — staff only)</span>
          </Label>
          <Textarea
            id="cancellation-internal-note"
            className="mt-1.5"
            rows={3}
            maxLength={MAX_CANCEL_NOTE_LENGTH}
            value={internalNote}
            disabled={busy}
            placeholder="e.g. Duplicate of an earlier booking"
            onChange={event => setInternalNote(event.target.value)}
          />
          <p className="mt-1.5 text-xs text-muted-foreground">
            Why it was cancelled, for staff. Never shown or sent to the customer.
          </p>
        </div>

        <div>
          <Label htmlFor="cancellation-external-note" className="text-xs font-medium">
            Note for the customer <span className="text-muted-foreground">(optional — sent to the customer)</span>
          </Label>
          <Textarea
            id="cancellation-external-note"
            className="mt-1.5"
            rows={3}
            maxLength={MAX_CANCEL_NOTE_LENGTH}
            value={externalNote}
            disabled={busy}
            placeholder="e.g. Our office is closed that day. Please call us to rebook."
            onChange={event => setExternalNote(event.target.value)}
          />
          <p className="mt-1.5 text-xs text-muted-foreground">
            {`${attendeeName ?? "The customer"} is told the appointment is cancelled by text, call or email, whichever they gave us. Anything written here is included in that message.`}
          </p>
        </div>
      </section>

      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}

      <AppointmentDialogFooter>
        <p className="flex-1 text-xs text-muted-foreground sm:text-left">
          This cannot be undone. The slot goes back on offer.
        </p>
        <Button
          variant="outline"
          disabled={busy}
          onClick={onDone}
        >
          Keep appointment
        </Button>
        <Button variant="destructive" disabled={busy || problem !== null} onClick={submitCancellation}>
          {release
            ? cancelState.isLoading ? "Releasing…" : "Release slot"
            : cancelState.isLoading ? "Cancelling…" : "Cancel appointment"}
        </Button>
      </AppointmentDialogFooter>
    </>
  )
}

/** Turns a failed cancellation into something worth reading. */
function describeCancelFailure(error: unknown): string {
  const failure = error as { status?: number | string; data?: unknown }
  const detail = extractApiError(failure?.data)

  if (failure?.status === 403) return "You do not have permission to cancel appointments."
  if (failure?.status === 404) return "This appointment is no longer in the scheduling system."
  if (detail) return detail

  return "The appointment could not be cancelled."
}
